import { Icon } from '@blueprintjs/core'
import React, { useEffect, useMemo, useState } from 'react'
import { cnrTabs } from 'redux/app/vessel/onboard/constants';
import classNames from 'classnames';
import { TCNRAllColumns, TCNRColumn } from 'redux/app/vessel/onboard/types';
import TabNavigation from '../../../_elements/TabNavigation';
import { getStoredColumns, saveStoredColumns } from 'redux/app/vessel/onboard/utils';

type OwnProps = {
	onClose: () => void;
	setColumns: (columns: any) => void;
	selectedColumns: any;
	allColumns: TCNRAllColumns;
	activeTab: string;
	vesselType: string;
	vesselFuelTypes: string[];
	vesselId: string;
}
function CustomizeColumns(props: OwnProps) {
	const {
		selectedColumns,
		setColumns,
		allColumns,
		vesselType,
		vesselFuelTypes,
		vesselId,
	} = props;

	const [activeTab, setActiveTab] = useState<string>(props.activeTab);

	const [searchKey, setSearchKey] = useState<string>("");
	const [isSavingColumns, setIsSavingColumns] = useState<boolean>(false);

	const signalStatusColumn = { label: "Signal Status", isDefault: true, ids: {} };
	const imosStatusColumn = { label: "IMOS Status", isDefault: true, ids: {} };
	const docStatusColumn = { label: "Doc Status", isDefault: true, ids: {} };

	const specialColumns: Record<string, typeof signalStatusColumn[]> = {
		cargo: [signalStatusColumn, docStatusColumn],
		bunker: [signalStatusColumn, docStatusColumn],
		sofForms: [imosStatusColumn, docStatusColumn],
		stoppages: [imosStatusColumn, docStatusColumn],
	};

	const defaultStaticColumns = [signalStatusColumn, imosStatusColumn, docStatusColumn];

	const baseColumns = activeTab === "cargo"
		? (allColumns?.[activeTab]?.filter(column => column.subType ? column.subType === vesselType : true) ?? [])
		: (allColumns?.[activeTab] ?? []);

	const availableColumns = [
		...baseColumns,
		...(specialColumns[activeTab] ?? defaultStaticColumns),
	];

	const filteredData = useMemo(() => {
		return availableColumns.filter(column => column.label.toLowerCase().includes(searchKey.toLowerCase().trim()));
	}, [searchKey, activeTab]);

	const handleCheckboxChange = (e: any) => {
		const { value, checked } = e.target;
		const temp = {
			...selectedColumns,
			[activeTab]: [
				...selectedColumns?.[activeTab] || [],
				value
			]
		}
		if (checked) {
			setColumns(temp);
		} else {
			setColumns({
				...temp,
				[activeTab]: temp?.[activeTab]?.filter((column: any) => column !== value)
			});
		}
	};

	const handleSave = () => {
		setIsSavingColumns(true);
		saveStoredColumns(selectedColumns);
		setTimeout(() => {
			setIsSavingColumns(false);
			props.onClose();
		}, 300)
	}

	const handleSelectAll = () => {
		setColumns({
			...selectedColumns,
			[activeTab]: availableColumns.map(item => item.label)
		})
	}

	const handleReset = () => {
		const defaultColumns = availableColumns?.filter((column: TCNRColumn) => {
			if (column?.fuelType) {
				return vesselFuelTypes?.includes(column.fuelType);
			}
			return column?.isDefault;
		})?.map(column => column.label);
		setColumns({
			...selectedColumns,
			[activeTab]: defaultColumns
		});
	}

	useEffect(() => {
		setActiveTab(props.activeTab);
	}, [props.activeTab])

	useEffect(() => {
		// Show default columns as selected when no cutomization is saved locally
		const parsedValues = getStoredColumns();

		if (parsedValues?.[activeTab]) return;

		const defaultColumns = availableColumns?.filter((column: TCNRColumn) => {
			if (column?.fuelType) {
				return vesselFuelTypes?.includes(column.fuelType);
			}
			return column?.isDefault;
		})?.map(column => column.label);

		const newColumns = {
			...parsedValues,
			[activeTab]: defaultColumns
		};
		setColumns(newColumns);
		saveStoredColumns(newColumns);
	}, [activeTab])

	// When vesselId or activetab changes, need to check if the saved columns are still valid for the new vessel for fuel types
	useEffect(() => {
		const parsedValues = getStoredColumns();

		const allFuelTypeLabels = availableColumns
			.filter((col: TCNRColumn) => col.fuelType)
			.map(col => col.label);

		const matchingFuelTypeLabels = availableColumns
			.filter((col: TCNRColumn) => col.fuelType && vesselFuelTypes?.includes(col.fuelType))
			.map(col => col.label);

		const updatedColumns = {
			...parsedValues,
			[activeTab]: [
				...parsedValues?.[activeTab]?.filter((col: string) => !allFuelTypeLabels.includes(col)),
				...matchingFuelTypeLabels,
			],
		};

		if (activeTab === "cargo") {
			const matchingCargoTypes = availableColumns
				?.filter((col: TCNRColumn) => col.subType && col.subType === vesselType)
				.map(col => col.label);
			const allCargoTypes = allColumns?.[activeTab]
				?.filter((col: TCNRColumn) => col.subType)
				.map(col => col.label);

			updatedColumns[activeTab] = [
				...updatedColumns?.[activeTab]?.filter((col: string) => !allCargoTypes.includes(col)),
				...matchingCargoTypes,
			]
		}
		setColumns(updatedColumns);
		saveStoredColumns(updatedColumns);
	}, [vesselId, activeTab]);

	return (
		<div className='flex flex-col gap-4 h-1/2'>
			<div className='flex gap-4 items-center border-b p-4'>
				<Icon icon={"cog"} size={20} className='border p-2 rounded-lg h-12 w-12 flex items-center justify-center' />
				<div className='flex flex-col w-full'>
					<div className="flex justify-between items-center">
						<p className='font-semibold text-gray-900'>Customize Columns</p>
						<Icon onClick={props.onClose} icon="cross" className='text-gray-600 cursor-pointer' />
					</div>
					<p className='text-sm text-gray-600'>
						Select which columns to display in the CNR table.
						Currently showing {filteredData?.length} of {availableColumns.length} available fields.
					</p>
				</div>
			</div>
			<div className='w-full px-4 relative'>
				<Icon icon="search" className='absolute left-7 top-1/2 transform -translate-y-1/2 text-gray-600' />
				<input
					type='search'
					className='border rounded-lg w-full p-2 pl-10 placeholder:text-gray-600'
					placeholder='Search fields'
					onChange={(e) => {
						const value = e.target.value;
						setSearchKey(value);
					}}
				/>
			</div>
			<div className='px-4 flex flex-col gap-4 md:gap-2'>
				<div className="flex flex-col gap-2 sm:flex-row sm:gap-0 justify-between">
					<div className='flex gap-2 items-center'>
						<p className='font-medium'>Selected Columns</p>
						<span className='border rounded-md px-1 shadow text-sm text-gray-800'>{`${selectedColumns?.[activeTab]?.length || 0}/${availableColumns.length}`} selected</span>
					</div>
					<div className="flex gap-2">
						<button
							className='font-medium border rounded-full px-2 shadow'
							onClick={handleReset}
						>
							Reset to default
						</button>
						<button
							className='font-medium border rounded-full px-2 shadow'
							onClick={handleSelectAll}
						>
							Select All
						</button>
					</div>
				</div>
				<div className='flex flex-wrap gap-2 overflow-scroll h-20 md:h-full ws-scroll'>
					{selectedColumns?.[activeTab]?.map((column: any) => (
						<div
							className='flex gap-2 justify-between items-center border rounded-full px-2 bg-gray-100'
							key={column}
						>
							<span className=''>{column}</span>
							<Icon
								icon="cross"
								size={12}
								className='text-gray-600 cursor-pointer'
								onClick={() => {
									setColumns({
										...selectedColumns,
										[activeTab]: selectedColumns?.[activeTab]?.filter((item: any) => item !== column)
									})
								}}
							/>
						</div>
					))}
				</div>
			</div>

			<div className='border-b'>
				<div className='mx-4 border-t' />
				<TabNavigation
					tabs={cnrTabs}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
					className='mb-2 px-4'
				/>
				<div className='px-4 flex justify-start flex-wrap overflow-y-scroll max-h-96 h-40 md:h-full ws-scroll'>
					{filteredData && filteredData.length > 0 ? filteredData.map((column: any) => (
						<div className='flex items-center gap-2 w-1/2 sm:w-1/3 md:w-1/4 mb-2' key={column.label}>
							<input
								type="checkbox"
								id={column.label}
								name={column.label}
								value={column.label}
								checked={selectedColumns?.[activeTab]?.includes(column.label) || false}
								onChange={handleCheckboxChange}
							/>
							<label htmlFor={column.label}>{column.label}</label>
						</div>
					))
						: <p className='text-center w-full py-4'>No matching columns, please refine your search</p>
					}
				</div>
			</div>

			<div className='flex gap-4 items-center justify-end p-4 pt-0'>
				<button
					className='bg-white border rounded-md py-2 px-4 shadow hover:bg-gray-100'
					disabled={isSavingColumns}
					onClick={props.onClose}
				>
					Cancel
				</button>
				<button
					className={classNames(
						' text-white border rounded-md py-2 px-4 shadow',
						{
							'bg-blue-400 cursor-not-allowed': isSavingColumns,
							'hover:bg-blue-700 bg-blue-600': !isSavingColumns,
						}
					)}
					onClick={handleSave}
					disabled={isSavingColumns}
				>
					{isSavingColumns ? "Saving..." : "Save Changes"}
				</button>
			</div>
		</div>
	)
}

export default CustomizeColumns